/* Component registries keep the markdown renderer exhaustive. */
/* eslint-disable react-refresh/only-export-components */
import {
  ArrowRight,
  Database,
  Globe,
  LockKeyhole,
  Server,
  UserRound,
  Workflow,
} from 'lucide-react';
import { useId, useState } from 'react';

import { SelectField } from '@/shared/ui/select';

import type { ReactNode } from 'react';

function Choice({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly (readonly [string, string])[];
}) {
  return (
    <label className="lab-field">
      <span>{label}</span>
      <SelectField
        aria-label={label}
        value={value}
        onValueChange={onChange}
        options={options.map(([id, text]) => ({ value: id, label: text }))}
      />
    </label>
  );
}
function Observation({ children }: { children: ReactNode }) {
  return (
    <div className="lab-observation" role="status">
      {children}
    </div>
  );
}
function Cards({ items }: { items: { label: string; value: ReactNode; active?: boolean }[] }) {
  return (
    <div className="app-model-cards">
      {items.map((i) => (
        <div key={i.label} data-active={i.active}>
          <span>{i.label}</span>
          <strong>{i.value}</strong>
        </div>
      ))}
    </div>
  );
}

export function RelationshipMap() {
  const [project, setProject] = useState('1'),
    [policy, setPolicy] = useState('member'),
    [user, setUser] = useState('u2'),
    [result, setResult] = useState(''),
    [rows, setRows] = useState<string[]>([]);
  const members = project === '1' ? ['u1', 'u2', 'u3'] : ['u3', 'u4'];
  const task = project === '1' ? '11' : '21';
  const reset = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setResult('');
  };
  return (
    <>
      <p>Try a weaker rule, inspect the stored result, then add the missing constraint.</p>
      <Choice
        label="Assignment rule"
        value={policy}
        onChange={(value) => {
          setPolicy(value);
          setResult('');
        }}
        options={[
          ['exists', 'Existing user only'],
          ['member', 'Same-project membership'],
        ]}
      />
      <div className="lab-two">
        <Choice
          label="Task's project"
          value={project}
          onChange={reset(setProject)}
          options={[
            ['1', 'Atlas · task 11'],
            ['2', 'Beacon · task 21'],
          ]}
        />
        <Choice
          label="Assignee"
          value={user}
          onChange={reset(setUser)}
          options={[
            ['u1', 'Asha · u1'],
            ['u2', 'Leo · u2'],
            ['u3', 'Mina · u3'],
            ['u4', 'Noor · u4'],
          ]}
        />
      </div>
      <div className="app-model-relationship">
        <div>
          <Database />
          <strong>PROJECT {project}</strong>
          <small>{project === '1' ? 'Atlas' : 'Beacon'}</small>
        </div>
        <div className="app-model-branches">
          <div>
            <Workflow />
            <strong>Task {task}</strong>
            <code>project_id = {project}</code>
          </div>
          <div>
            <UserRound />
            <strong>Memberships</strong>
            <code>{members.join(', ')}</code>
          </div>
        </div>
        <div className="app-model-join">
          <ArrowRight />
          <code>
            ({project}, {task}, {user})
          </code>
          <span>
            {policy === 'member'
              ? 'Both composite references must match.'
              : 'Only task and user existence are checked.'}
          </span>
        </div>
      </div>
      <button
        onClick={() => {
          const key = `${task} / ${user}`;
          if (policy === 'member' && !members.includes(user))
            setResult('Rejected: the user exists but is not a member of this project.');
          else if (rows.includes(key)) setResult('Rejected: duplicate task/user pair.');
          else {
            setRows([...rows, key]);
            setResult(
              members.includes(user)
                ? 'Inserted: both relationships hold; the task/user pair is unique.'
                : 'Inserted an invalid relationship: the user exists but belongs to another project. Existence alone is too weak.',
            );
          }
        }}
      >
        Attempt assignment
      </button>
      <button
        onClick={() => {
          setRows([]);
          setResult('Assignments cleared. Try the same input with the stronger rule.');
        }}
      >
        Reset assignments
      </button>
      <p>Stored assignments: {rows.length ? rows.join(' · ') : 'none'}</p>
      <Observation>
        {result || 'Predict which reference will permit or reject this assignment.'}
      </Observation>
    </>
  );
}
export function TransactionJournal() {
  const [atomic, setAtomic] = useState('yes'),
    [failure, setFailure] = useState('yes'),
    [step, setStep] = useState(0);
  const failed = step >= 2 && failure === 'yes';
  const project = atomic === 'no' ? step >= 1 : step >= 3 && !failed;
  const member = step >= (atomic === 'yes' ? 3 : 2) && !failed;
  return (
    <>
      <div className="lab-two">
        <Choice
          label="Transaction boundary"
          value={atomic}
          onChange={(v) => {
            setAtomic(v);
            setStep(0);
          }}
          options={[
            ['yes', 'One transaction'],
            ['no', 'Commit each write'],
          ]}
        />
        <Choice
          label="Manager insertion"
          value={failure}
          onChange={(v) => {
            setFailure(v);
            setStep(0);
          }}
          options={[
            ['yes', 'Fails'],
            ['no', 'Succeeds'],
          ]}
        />
      </div>
      <ol className="app-model-journal">
        {['Insert project', 'Insert manager', 'Commit or roll back'].map((s, i) => (
          <li key={s} data-current={step === i + 1} data-done={step > i}>
            <b>{i + 1}</b>
            {s}
            <span>{step <= i ? 'pending' : i === 1 && failed ? 'failed' : 'observed'}</span>
          </li>
        ))}
      </ol>
      <Cards
        items={[
          {
            label: 'Pending rows',
            value:
              atomic === 'yes' && step > 0 && step < 3
                ? failed
                  ? 'Transaction aborted; rollback required'
                  : step === 2
                    ? 'Project + manager uncommitted'
                    : '1 uncommitted project'
                : 'none',
          },
          { label: 'Committed projects', value: Number(project), active: project },
          { label: 'Committed managers', value: Number(member), active: member },
        ]}
      />
      <div className="lab-actions">
        <button disabled={step === 3} onClick={() => setStep(step + 1)}>
          Advance transaction
        </button>
        <button onClick={() => setStep(0)}>Reset transaction</button>
      </div>
      <Observation>
        {step < 3
          ? 'Predict which rows will survive the final outcome.'
          : failed
            ? atomic === 'yes'
              ? 'Rollback: neither required row remains.'
              : 'Partial outcome: project remains without its manager. The early commit cannot be rolled back here.'
            : 'Commit: project and manager both exist.'}
      </Observation>
    </>
  );
}
export function ContractGates() {
  const [scenario, setScenario] = useState('wrong-type'),
    [reveal, setReveal] = useState(false);
  const cases: Record<string, { body: string; failure: number; result: string }> = {
    'wrong-type': {
      body: '{"user_id":42}',
      failure: 0,
      result: '422 · user_id must be a string; no write.',
    },
    nonmember: {
      body: '{"user_id":"u4"}',
      failure: 1,
      result: '409 · Noor exists but is not an Atlas member; no write.',
    },
    duplicate: {
      body: '{"user_id":"u2"}',
      failure: 2,
      result: '409 · Leo is already assigned; one original row remains.',
    },
    valid: {
      body: '{"user_id":"u3"}',
      failure: 3,
      result: '201 · Mina is eligible; one new assignment.',
    },
  };
  const current = cases[scenario];
  if (!current) return null;
  return (
    <>
      <Choice
        label="Assignment request"
        value={scenario}
        onChange={(v) => {
          setScenario(v);
          setReveal(false);
        }}
        options={[
          ['wrong-type', 'Wrong field type'],
          ['nonmember', 'User outside project'],
          ['duplicate', 'Existing assignment'],
          ['valid', 'New eligible assignment'],
        ]}
      />
      <pre>
        POST /api/tasks/11/assignments{'\n'}
        {current.body}
      </pre>
      <div className="app-model-gates">
        {['Field schema', 'Project relationship', 'Pair uniqueness'].map((label, i) => (
          <div
            key={label}
            data-result={
              !reveal
                ? 'pending'
                : i < current.failure
                  ? 'pass'
                  : i === current.failure
                    ? 'fail'
                    : 'pending'
            }
          >
            <b>{i + 1}</b>
            <span>{label}</span>
            <small>
              {!reveal
                ? 'Predict'
                : i < current.failure
                  ? 'Pass'
                  : i === current.failure
                    ? 'Reject'
                    : 'Not reached'}
            </small>
          </div>
        ))}
      </div>
      <button onClick={() => setReveal(true)}>Evaluate request</button>
      <Observation>
        {reveal
          ? current.result
          : 'Assume the actor is already authorized. Which later boundary rejects this body?'}
      </Observation>
    </>
  );
}
export function PageWindow() {
  const [offset, setOffset] = useState(0),
    [mode, setMode] = useState('offset'),
    [after, setAfter] = useState<number[]>([0]),
    [size, setSize] = useState('2'),
    [filter, setFilter] = useState('all'),
    [inserted, setInserted] = useState(false);
  const ids = (inserted ? [10, 11, 12, 13, 14, 15] : [11, 12, 13, 14, 15]).filter(
    (id) => filter === 'all' || id % 2 === 1,
  );
  const cursor = after[after.length - 1] ?? 0;
  const remaining = mode === 'offset' ? ids.slice(offset) : ids.filter((id) => id > cursor);
  const page = remaining.slice(0, Number(size));
  function firstPage() {
    setOffset(0);
    setAfter([0]);
  }
  return (
    <>
      <Choice
        label="Pagination strategy"
        value={mode}
        onChange={(value) => {
          setMode(value);
          firstPage();
        }}
        options={[
          ['offset', 'Offset'],
          ['cursor', 'Cursor'],
        ]}
      />
      <div className="lab-two">
        <Choice
          label="Status filter"
          value={filter}
          onChange={(v) => {
            setFilter(v);
            firstPage();
          }}
          options={[
            ['all', 'All tasks'],
            ['open', 'Open · odd IDs'],
          ]}
        />
        <Choice
          label="Page size"
          value={size}
          onChange={(v) => {
            setSize(v);
            firstPage();
          }}
          options={[
            ['1', '1'],
            ['2', '2'],
            ['3', '3'],
          ]}
        />
      </div>
      <p>Ordered matching IDs</p>
      <div className="app-model-strip">
        {ids.map((id) => (
          <span key={id} data-selected={page.includes(id)}>
            {id}
          </span>
        ))}
      </div>
      <Cards
        items={[
          {
            label: mode === 'offset' ? 'Offset' : 'Cursor after ID',
            value: mode === 'offset' ? offset : cursor,
          },
          { label: 'Response items', value: page.join(', ') || '[]' },
          { label: 'has_more', value: String(remaining.length > Number(size)) },
        ]}
      />
      <div className="lab-actions">
        <button
          disabled={mode === 'offset' ? offset === 0 : after.length === 1}
          onClick={() =>
            mode === 'offset'
              ? setOffset(Math.max(0, offset - Number(size)))
              : setAfter(after.slice(0, -1))
          }
        >
          Previous page
        </button>
        <button
          disabled={remaining.length <= Number(size)}
          onClick={() =>
            mode === 'offset'
              ? setOffset(offset + Number(size))
              : setAfter([...after, page[page.length - 1] ?? cursor])
          }
        >
          Next page
        </button>
        <button disabled={inserted} onClick={() => setInserted(true)}>
          Insert earlier ID 10
        </button>
        <button
          onClick={() => {
            setInserted(false);
            firstPage();
          }}
        >
          Reset dataset
        </button>
      </div>
      <pre>
        {mode === 'offset'
          ? `ORDER BY id LIMIT ${size} OFFSET ${offset}`
          : `WHERE id > ${cursor} ORDER BY id LIMIT ${size}`}
      </pre>
      <Observation>
        {inserted
          ? mode === 'offset'
            ? 'The offset stayed fixed while the ordered dataset changed. Compare the visible IDs; a later page can now repeat a previously seen row.'
            : 'The cursor resumes after a fixed ID. An earlier insert does not shift this window; that earlier row is also not revisited. This is not a snapshot.'
          : 'View page one, advance to page two, then insert an earlier row. Predict which ID moves.'}
      </Observation>
      <details>
        <summary>What an index changes</summary>
        <p>
          An index on the query's filter/order columns can reduce lookup work. It does not freeze
          pagination or force a particular plan. PostgreSQL chooses a plan using its estimates;
          measure with EXPLAIN.
        </p>
      </details>
    </>
  );
}
export function DocumentCompare() {
  const [mode, setMode] = useState('embedded'),
    [name, setName] = useState('Asha'),
    [copies, setCopies] = useState(['Asha', 'Asha']);
  return (
    <>
      <Choice
        label="Task representation"
        value={mode}
        onChange={setMode}
        options={[
          ['embedded', 'Embed current username'],
          ['reference', 'Reference user ID'],
        ]}
      />
      <div className="app-model-document">
        <div>
          <h4>Central user</h4>
          <pre>{JSON.stringify({ id: 'u1', name }, null, 2)}</pre>
        </div>
        <ArrowRight />
        <div>
          <h4>Two task views</h4>
          {copies.map((copy, i) => (
            <pre key={i}>
              {mode === 'embedded'
                ? JSON.stringify({ task: i + 11, user: { id: 'u1', name: copy } }, null, 2)
                : `task ${i + 11} → user_id u1 → ${name}`}
            </pre>
          ))}
        </div>
      </div>
      <div className="lab-actions">
        <button onClick={() => setName('Asha Rao')}>Rename central user</button>
        <button onClick={() => setCopies([name, copies[1] ?? 'Asha'])}>
          Refresh first embedded copy
        </button>
        <button onClick={() => setCopies([name, name])}>Refresh both copies</button>
        <button
          onClick={() => {
            setName('Asha');
            setCopies(['Asha', 'Asha']);
          }}
        >
          Reset documents
        </button>
      </div>
      <Observation>
        {mode === 'reference'
          ? 'Both views resolve the current central name. This needs a lookup strategy; references do not automatically enforce relational foreign keys.'
          : copies.some((c) => c !== name)
            ? 'Stale embedded value: changing the central record did not update every copy. Decide who owns that synchronization.'
            : 'The copies currently agree. Predict what happens if only the central user changes.'}
      </Observation>
    </>
  );
}
export function SessionTimeline() {
  const [cookie, setCookie] = useState(false),
    [record, setRecord] = useState('none'),
    [result, setResult] = useState('No identity established.');
  return (
    <>
      <div className="app-model-session">
        <div>
          <Globe />
          <h4>Browser</h4>
          <code>{cookie ? 'opaque handle: session-7' : 'no session cookie'}</code>
          <small>Secure · HttpOnly · host-only</small>
        </div>
        <ArrowRight />
        <div>
          <Server />
          <h4>Session store</h4>
          <code>
            {record === 'valid'
              ? 'session-7 → u1'
              : record === 'expired'
                ? 'session-7 → expired'
                : 'no session record'}
          </code>
          <small>Identity resolved on requests</small>
        </div>
      </div>
      <div className="lab-actions">
        <button
          onClick={() => {
            setCookie(true);
            setRecord('valid');
            setResult('Login: credentials checked, opaque handle issued, server record created.');
          }}
        >
          Sign in Asha
        </button>
        <button
          onClick={() =>
            setResult(
              cookie && record === 'valid'
                ? '200 · current user u1. Resource permission still needs checking.'
                : '401 · no valid session resolves this request.',
            )
          }
        >
          Send protected request
        </button>
        <button
          disabled={record !== 'valid'}
          onClick={() => {
            setRecord('expired');
            setResult(
              'Server expiration changed; browser still has the handle. Predict the next request.',
            );
          }}
        >
          Expire server session
        </button>
        <button
          onClick={() => {
            setCookie(false);
            setRecord('none');
            setResult('Logout removes the server record and clears the browser cookie.');
          }}
        >
          Log out
        </button>
      </div>
      <Observation>{result}</Observation>
    </>
  );
}
export function TokenGates() {
  const [scenario, setScenario] = useState('valid'),
    [show, setShow] = useState(false);
  const stages = [
    'Signature / permitted algorithm',
    'Expected issuer',
    'Expected audience',
    'Required lifetime',
    'Known subject',
  ];
  const failure: Record<string, number> = {
    valid: 5,
    tampered: 0,
    issuer: 1,
    audience: 2,
    expired: 3,
    unknown: 4,
  };
  const at = failure[scenario] ?? 0;
  return (
    <>
      <Choice
        label="Synthetic token case"
        value={scenario}
        onChange={(v) => {
          setScenario(v);
          setShow(false);
        }}
        options={[
          ['valid', 'Valid'],
          ['tampered', 'Payload edited'],
          ['issuer', 'Wrong issuer'],
          ['audience', 'Wrong audience'],
          ['expired', 'Expired'],
          ['unknown', 'Unknown subject'],
        ]}
      />
      <div className="app-model-token">
        <span>HEADER</span>
        <span>PAYLOAD · readable claims</span>
        <span>SIGNATURE</span>
      </div>
      <pre>
        {JSON.stringify(
          {
            sub: scenario === 'unknown' ? 'u999' : 'u1',
            iss: scenario === 'issuer' ? 'other' : 'mio-training',
            aud: scenario === 'audience' ? 'other-api' : 'project-api',
            exp: scenario === 'expired' ? 'past' : 'future',
          },
          null,
          2,
        )}
      </pre>
      <ol className="app-model-checklist">
        {stages.map((stage, i) => (
          <li
            key={stage}
            data-result={!show ? 'pending' : i < at ? 'pass' : i === at ? 'fail' : 'pending'}
          >
            <LockKeyhole size={16} />
            {stage}
            <strong>{!show ? '?' : i < at ? 'Pass' : i === at ? 'Reject' : 'Not reached'}</strong>
          </li>
        ))}
      </ol>
      <button onClick={() => setShow(true)}>Verify synthetic claims</button>
      <Observation>
        {!show
          ? 'Readable claims are untrusted until the required checks pass.'
          : at === 5
            ? 'Identity established under this policy. Current project membership is still a separate check.'
            : '401 · rejected before protected work. This model illustrates decisions; Workshop 2 uses real library verification.'}
      </Observation>
    </>
  );
}
const memberships: Record<string, Record<string, string>> = {
  asha: { '1': 'manager' },
  leo: { '1': 'viewer' },
  mina: { '1': 'editor', '2': 'manager' },
  noor: { '2': 'viewer' },
};
export function PermissionMatrix() {
  const [user, setUser] = useState('leo'),
    [project, setProject] = useState('1'),
    [action, setAction] = useState('write');
  const role = memberships[user]?.[project];
  const allowed = !!role && (action === 'read' || role !== 'viewer');
  return (
    <>
      <div className="lab-two">
        <Choice
          label="Authenticated user"
          value={user}
          onChange={setUser}
          options={Object.keys(memberships).map((u) => [u, u])}
        />
        <Choice
          label="Resource project"
          value={project}
          onChange={setProject}
          options={[
            ['1', 'Atlas'],
            ['2', 'Beacon'],
          ]}
        />
      </div>
      <Choice
        label="Requested action"
        value={action}
        onChange={setAction}
        options={[
          ['read', 'Read tasks'],
          ['write', 'Create/update task'],
        ]}
      />
      <table className="lab-records">
        <thead>
          <tr>
            <th>Account</th>
            <th>Atlas</th>
            <th>Beacon</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(memberships).map(([u, roles]) => (
            <tr key={u} data-match={u === user}>
              <th>{u}</th>
              <td>{roles['1'] ?? 'none'}</td>
              <td>{roles['2'] ?? 'none'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Cards
        items={[
          { label: 'Current membership', value: role ?? 'absent' },
          { label: 'Decision', value: allowed ? 'Allow' : role ? '403' : '404', active: allowed },
        ]}
      />
      <Observation>
        {allowed
          ? `${role} permits ${action} in this project. The API still validates the requested data.`
          : role
            ? 'The session is valid, but a viewer cannot write. No state changes.'
            : 'Valid identity does not grant access to this project. Use the documented scoped 404.'}
      </Observation>
    </>
  );
}
export function BrowserBoundaries() {
  const [which, setWhich] = useState('cors');
  const cases: Record<string, { facts: string[]; decision: string; why: string }> = {
    cors: {
      facts: ['Different origin', 'Simple request was sent', 'No CORS read permission'],
      decision: 'Browser blocks response access',
      why: 'The server may have processed this request. CORS is not proof of no side effect and does not authorize terminal clients.',
    },
    csrf: {
      facts: ['Valid session cookie', 'Write request', 'Missing CSRF evidence'],
      decision: 'Server rejects · 403',
      why: 'Automatic cookie attachment did not establish an intentional protected action. Check CSRF before changing state.',
    },
    role: {
      facts: ['Valid session', 'Correct CSRF token', 'Viewer role'],
      decision: 'Server rejects · 403',
      why: 'Identity and CSRF checks passed; the project permission still forbids this write.',
    },
    allowed: {
      facts: ['Valid session', 'Correct CSRF token', 'Editor role in this project'],
      decision: 'Server permits the write',
      why: 'Identity, CSRF and project permission all pass. A correct policy must preserve this allowed path as well as reject unauthorized work.',
    },
    text: {
      facts: ['Task title contains <script>', 'Rendered with textContent', 'No HTML parsing'],
      decision: 'Display literal text',
      why: 'The value becomes a text node. Do not replace this with untrusted innerHTML.',
    },
  };
  const item = cases[which];
  if (!item) return null;
  return (
    <>
      <Choice
        label="Security scenario"
        value={which}
        onChange={setWhich}
        options={[
          ['cors', 'Cross-origin response'],
          ['csrf', 'Cookie-backed forged write'],
          ['role', 'Viewer attempts write'],
          ['allowed', 'Allowed editor write'],
          ['text', 'HTML-like task title'],
        ]}
      />
      <div className="app-model-boundary">
        <div>
          <Globe />
          <h4>Browser boundary</h4>
          <span>Origin · rendering · cookie behavior</span>
        </div>
        <div>
          <LockKeyhole />
          <h4>Server boundary</h4>
          <span>Session · CSRF · resource permission</span>
        </div>
      </div>
      <ul>
        {item.facts.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
      <Observation>
        <strong>{item.decision}.</strong> {item.why}
      </Observation>
    </>
  );
}
export function DomEvent() {
  const id = useId();
  const [input, setInput] = useState('Review contract'),
    [value, setValue] = useState(''),
    [event, setEvent] = useState(false);
  return (
    <>
      <label className="lab-field" htmlFor={id}>
        <span>Modeled form value</span>
        <input id={id} value={input} onChange={(e) => setInput(e.target.value)} />
      </label>
      <button
        onClick={() => {
          setValue(input.trim());
          setEvent(true);
        }}
      >
        Dispatch submit event
      </button>
      <div className="app-model-dom">
        <div>
          <h4>Handler</h4>
          <pre>
            {
              'event.preventDefault();\nconst title = input.value.trim();\nmessage.textContent = title;'
            }
          </pre>
        </div>
        <div>
          <h4>DOM preview</h4>
          <div className="app-model-browser">
            <div>message · text node</div>
            <p>{event ? value || '(empty text)' : 'No event yet'}</p>
          </div>
        </div>
      </div>
      <Cards
        items={[
          { label: 'Input property', value: JSON.stringify(input) },
          { label: 'Handler value', value: event ? JSON.stringify(value) : 'not called' },
        ]}
      />
      <Observation>
        {event
          ? 'The handler read the input and updated a DOM text node. No API request or database write occurred.'
          : 'Try <b>hello</b>, then submit. Predict whether it becomes bold markup or literal text.'}
      </Observation>
    </>
  );
}
export function PromiseExchange() {
  const [result, setResult] = useState('422'),
    [step, setStep] = useState(0);
  const network = result === 'network';
  const noBody = result === '204';
  const note =
    step === 0
      ? 'fetch starts: promise pending; no Response yet.'
      : step === 1
        ? network
          ? 'Promise rejects: no readable HTTP Response.'
          : 'fetch fulfills with a Response. Inspect its status before deciding success.'
        : network
          ? 'catch reports the network failure; do not invent an HTTP status.'
          : noBody
            ? '204: return null without response.json(). Cleanup ends pending state.'
            : result === '422'
              ? 'Parsed error body → throw an application error with status 422. Preserve input.'
              : 'Parsed successful JSON → return data, then update the interface.';
  return (
    <>
      <Choice
        label="Request outcome"
        value={result}
        onChange={(v) => {
          setResult(v);
          setStep(0);
        }}
        options={[
          ['200', '200 · JSON success'],
          ['422', '422 · validation error'],
          ['204', '204 · no content'],
          ['network', 'Network failure'],
        ]}
      />
      <div className="app-model-promise-track">
        {['Pending', 'Response / rejection', 'Body / caller outcome'].map((t, i) => (
          <div key={t} data-active={i === step}>
            <b>{i + 1}</b>
            {t}
          </div>
        ))}
      </div>
      <Cards
        items={[
          { label: 'Promise', value: step === 0 ? 'pending' : network ? 'rejected' : 'fulfilled' },
          { label: 'Response status', value: step === 0 || network ? 'none' : result },
          {
            label: 'response.ok',
            value: step === 0 || network ? 'not available' : String(result !== '422'),
          },
        ]}
      />
      <div className="lab-actions">
        <button disabled={step === 2} onClick={() => setStep(step + 1)}>
          Advance request
        </button>
        <button onClick={() => setStep(0)}>Reset request</button>
      </div>
      <Observation>{note}</Observation>
    </>
  );
}
export function ResponseRace() {
  const [guard, setGuard] = useState('yes'),
    [pending, setPending] = useState<string[]>([]),
    [selected, setSelected] = useState('none'),
    [display, setDisplay] = useState('none'),
    [history, setHistory] = useState<string[]>([]),
    [sequence, setSequence] = useState(0),
    [latest, setLatest] = useState('');
  const start = (project: string) => {
    const id = `${project}-${sequence + 1}`;
    setSequence(sequence + 1);
    setLatest(id);
    setPending([...pending, id]);
    setSelected(project);
    setHistory([...history, `Started ${id}`]);
  };
  const deliver = (id: string) => {
    const project = id.split('-')[0] ?? '';
    const accept = guard === 'no' || latest === id;
    setPending(pending.filter((p) => p !== id));
    if (accept) setDisplay(project);
    setHistory([...history, `${id} delivered → ${accept ? 'displayed' : 'ignored as stale'}`]);
  };
  return (
    <>
      <Choice
        label="Response acceptance policy"
        value={guard}
        onChange={(v) => {
          setGuard(v);
          setPending([]);
          setSelected('none');
          setDisplay('none');
          setHistory([]);
        }}
        options={[
          ['yes', 'Only latest generation'],
          ['no', 'Every arriving response'],
        ]}
      />
      <div className="lab-actions">
        <button onClick={() => start('Atlas')}>Start Atlas request A</button>
        <button onClick={() => start('Beacon')}>Start Beacon request B</button>
      </div>
      <div className="app-model-inflight">
        {pending.length ? (
          pending.map((id) => (
            <button key={id} onClick={() => deliver(id)}>
              Deliver {id}
            </button>
          ))
        ) : (
          <p>No responses waiting.</p>
        )}
      </div>
      <Cards
        items={[
          { label: 'User selection', value: selected },
          {
            label: 'Displayed records',
            value: display,
            active: selected === display && display !== 'none',
          },
        ]}
      />
      <ol className="app-model-log">
        {history.slice(-6).map((h, i) => (
          <li key={`${i}-${h}`}>{h}</li>
        ))}
      </ol>
      <Observation>
        {selected !== display && display !== 'none'
          ? 'Mismatch: displayed records belong to an older request. A successful HTTP response can still be wrong for the current UI.'
          : 'Start A, start B, deliver B, then deliver A. Compare policies; the generation guard also needs to protect errors and loading cleanup.'}
      </Observation>
    </>
  );
}
export function AsyncLanes() {
  const [mode, setMode] = useState('cooperative'),
    [latency, setLatency] = useState('200');
  const time = Number(latency);
  const total = mode === 'cooperative' ? time : time * 2;
  return (
    <>
      <div className="lab-two">
        <Choice
          label="Scheduling model"
          value={mode}
          onChange={setMode}
          options={[
            ['cooperative', 'Two cooperative waits'],
            ['sequential', 'Sequential await'],
            ['blocking', 'Blocking calls on loop'],
          ]}
        />
        <Choice
          label="Dependency wait"
          value={latency}
          onChange={setLatency}
          options={[
            ['200', '200 ms'],
            ['400', '400 ms'],
            ['800', '800 ms'],
          ]}
        />
      </div>
      <svg
        className="app-model-timeline"
        viewBox="0 0 520 210"
        role="img"
        aria-label={`Conceptual schedule: ${mode}; total dependency wait ${total} milliseconds`}
      >
        <title>Waiting versus blocking</title>
        <text x="12" y="35">
          Call A
        </text>
        <text x="12" y="90">
          Call B
        </text>
        <text x="12" y="145">
          Health
        </text>
        <line x1="100" y1="175" x2="500" y2="175" stroke="currentColor" />
        <rect x="100" y="12" width="180" height="32" rx="7" fill="var(--app-model-blue)" />
        <rect
          x={mode === 'cooperative' ? 100 : 290}
          y="67"
          width="180"
          height="32"
          rx="7"
          fill="var(--app-model-purple)"
        />
        <rect
          x={mode === 'blocking' ? 475 : 145}
          y="122"
          width="18"
          height="30"
          rx="5"
          fill="var(--app-model-green)"
        />
        <text x="100" y="199">
          0
        </text>
        <text x="282" y="199">
          {time} ms
        </text>
        <text x="458" y="199">
          {2 * time} ms
        </text>
      </svg>
      <Cards
        items={[
          { label: 'Combined wait', value: `~${total} ms` },
          {
            label: 'Health scheduling',
            value: mode === 'blocking' ? 'waits for blocked loop' : 'can run while I/O waits',
          },
        ]}
      />
      <Observation>
        {mode === 'blocking'
          ? 'A blocking operation occupies the event-loop thread. async def alone does not fix it.'
          : mode === 'sequential'
            ? 'Each wait yields, so health can run; the two dependency waits still happen one after the other.'
            : 'Independent waits overlap. This is concurrency, not proof of CPU parallelism.'}{' '}
        The timeline omits overhead and is not a performance benchmark.
      </Observation>
    </>
  );
}
export function SocketState() {
  const [connected, setConnected] = useState(false),
    [allowed, setAllowed] = useState('yes'),
    [refresh, setRefresh] = useState('yes'),
    [server, setServer] = useState(1),
    [client, setClient] = useState(1),
    [note, setNote] = useState('Both views begin at version 1.');
  return (
    <>
      <div className="lab-two">
        <Choice
          label="Subscription permission"
          value={allowed}
          onChange={(v) => {
            setAllowed(v);
            setConnected(false);
          }}
          options={[
            ['yes', 'Project member'],
            ['no', 'Unrelated user'],
          ]}
        />
        <Choice
          label="On ready / reconnect"
          value={refresh}
          onChange={setRefresh}
          options={[
            ['yes', 'Refetch current state'],
            ['no', 'Only open connection'],
          ]}
        />
      </div>
      <div className="app-model-session">
        <div>
          <Server />
          <h4>Authoritative task</h4>
          <strong>version {server}</strong>
        </div>
        <div className="app-model-wire" data-connected={connected}>
          {connected ? 'CONNECTED' : 'DISCONNECTED'}
        </div>
        <div>
          <Globe />
          <h4>Client view</h4>
          <strong>version {client}</strong>
        </div>
      </div>
      <div className="lab-actions">
        <button
          onClick={() => {
            if (allowed === 'no') {
              setNote('Subscription rejected: a valid identity is insufficient for this project.');
              return;
            }
            setConnected(true);
            if (refresh === 'yes') setClient(server);
            setNote(
              refresh === 'yes'
                ? 'Ready → refetch; view converges to current state.'
                : 'Connection opened, but missed notifications were not replayed.',
            );
          }}
        >
          Connect / reconnect
        </button>
        <button
          onClick={() => {
            setConnected(false);
            setNote('Disconnected: future notifications cannot reach this client.');
          }}
        >
          Disconnect client
        </button>
        <button
          onClick={() => {
            setServer(server + 1);
            if (connected) setClient(server + 1);
            setNote(
              connected
                ? 'Committed change → notification → refetch.'
                : 'Server committed a change while client was disconnected.',
            );
          }}
        >
          Commit task change
        </button>
        <button
          onClick={() => {
            if (connected) setClient(server);
            setNote('A repeated refetch replaces state; it does not append another task.');
          }}
        >
          Deliver duplicate notification
        </button>
      </div>
      <Observation>
        {note} {client !== server ? 'The client view is stale.' : ''}
      </Observation>
    </>
  );
}
export function RetryLedger() {
  const [delivery, setDelivery] = useState('yes'),
    [commit, setCommit] = useState('yes'),
    [response, setResponse] = useState('no'),
    [key, setKey] = useState('same'),
    [changed, setChanged] = useState(false),
    [records, setRecords] = useState<Record<string, { title: string; id: number }>>({}),
    [serverResult, setServerResult] = useState('No request processed'),
    [client, setClient] = useState('No request sent'),
    [count, setCount] = useState(0);
  function send() {
    if (delivery === 'no') {
      setServerResult('Request never arrived; state unchanged');
      setClient('No response · caller cannot know whether the server received this request');
      return;
    }
    if (commit === 'no') {
      setServerResult('Transaction rolled back; no new task or operation record');
      setClient(
        response === 'no'
          ? 'Response lost · caller cannot infer rollback'
          : '500 · rolled back; no task or key record',
      );
      return;
    }
    const id = key === 'same' ? 'operation-1' : `operation-${count + 2}`;
    const title = changed ? 'Different title' : 'Review contract';
    const old = records[id];
    if (old && old.title !== title) {
      setServerResult('409 · same key, different input; no new task');
      setClient(
        response === 'no'
          ? 'Response lost · caller cannot know whether the input was rejected'
          : '409 · same key, different input; no new task',
      );
      return;
    }
    let result = old;
    if (!old) {
      result = { title, id: count + 1 };
      setRecords({ ...records, [id]: result });
      setCount(count + 1);
    }
    setServerResult(`${old ? 'Replayed' : 'Committed'} task ${result?.id}; stored result retained`);
    setClient(
      response === 'no'
        ? 'Response lost · caller cannot infer rollback'
        : `${old ? 'Replay' : 'Created'} · task ${result?.id}`,
    );
  }
  return (
    <>
      <div className="lab-two">
        <Choice
          label="Request delivery"
          value={delivery}
          onChange={setDelivery}
          options={[
            ['yes', 'Reaches server'],
            ['no', 'Lost before server'],
          ]}
        />
        <Choice
          label="Database outcome"
          value={commit}
          onChange={setCommit}
          options={[
            ['yes', 'Commit'],
            ['no', 'Rollback'],
          ]}
        />
        <Choice
          label="Response delivery"
          value={response}
          onChange={setResponse}
          options={[
            ['yes', 'Reaches client'],
            ['no', 'Lost after processing'],
          ]}
        />
        <Choice
          label="Operation key"
          value={key}
          onChange={setKey}
          options={[
            ['same', 'Reuse operation-1'],
            ['new', 'New key on each send'],
          ]}
        />
      </div>
      <label className="app-model-toggle">
        <input type="checkbox" checked={changed} onChange={(e) => setChanged(e.target.checked)} />{' '}
        Change the request title
      </label>
      <div className="lab-actions">
        <button onClick={send}>Send / retry creation</button>
        <button
          onClick={() => {
            setRecords({});
            setCount(0);
            setClient('No request sent');
            setServerResult('No request processed');
          }}
        >
          Reset ledger
        </button>
      </div>
      <Cards
        items={[
          { label: 'Committed tasks', value: count },
          { label: 'Operation records', value: Object.keys(records).length },
        ]}
      />
      <p>
        <strong>Server evidence:</strong> {serverResult}
      </p>
      <p>
        <strong>What the caller observed:</strong> {client}
      </p>
      <div className="app-model-ledger">
        {Object.entries(records).map(([k, r]) => (
          <div key={k}>
            <code>{k}</code>
            <ArrowRight size={16} />
            <span>
              task {r.id} · {r.title}
            </span>
          </div>
        ))}
      </div>
      <Observation>
        {client}. Same key reuses the stored result only when its scoped input matches. This model
        assumes the same authorized actor/project.
      </Observation>
    </>
  );
}
export function TestBoundaries() {
  const [claim, setClaim] = useState('permission'),
    [boundary, setBoundary] = useState('unit');
  const claims: Record<string, string> = {
    permission: 'An unrelated user cannot read Atlas tasks',
    race: 'An old response cannot overwrite the new project',
    preview: 'The real browser session reaches the deployed API',
  };
  const evidence: Record<string, Record<string, string>> = {
    permission: {
      unit: 'A pure role-function test can check its decisions, but does not prove routes actually invoke it.',
      integration:
        'A real TestClient request with another user and a real test database verifies route wiring and resource scoping.',
      browser:
        'A browser test exercises the full path; pair it with a direct-request denial so hidden buttons cannot conceal missing enforcement.',
    },
    race: {
      unit: 'A controller test resolves B before A deterministically and asserts the final selected project and records.',
      integration:
        'Backend integration tests cannot establish which response a frontend controller accepts.',
      browser:
        'A controlled browser interception can reproduce the race; random delays alone are weak evidence.',
    },
    preview: {
      unit: 'A mocked helper cannot prove DNS, TLS, cookies, gateway forwarding or the browser environment.',
      integration:
        'TestClient omits the real proxy/network path; a pass is useful but does not establish this claim.',
      browser:
        'Actual sign-in and a protected request through the preview exercise these boundaries. Record the observed outcome and limits.',
    },
  };
  return (
    <>
      <Choice
        label="Claim to verify"
        value={claim}
        onChange={setClaim}
        options={Object.entries(claims)}
      />
      <div className="app-model-test-levels">
        {[
          ['unit', 'Focused component'],
          ['integration', 'API + database'],
          ['browser', 'Actual browser preview'],
        ].map(([id, label]) => (
          <button key={id} aria-pressed={boundary === id} onClick={() => setBoundary(id ?? 'unit')}>
            <span>{id === 'unit' ? '1' : id === 'integration' ? '2' : '3'}</span>
            {label}
          </button>
        ))}
      </div>
      <Observation>{evidence[claim]?.[boundary]}</Observation>
      <p>
        A test name is not evidence of its boundary. State what actually ran, what was replaced and
        what the assertion established.
      </p>
    </>
  );
}
export const applicationModelTitles = {
  'relationship-map': 'Connect the relationship, inspect the constraint',
  'transaction-journal': 'What actually committed?',
  'contract-gates': 'Which boundary rejects this request?',
  'page-window': 'Move a window through ordered data',
  'document-compare': 'Who updates the duplicated fact?',
  'session-timeline': 'Follow a browser session',
  'token-gates': 'Decode is not verify',
  'permission-matrix': 'Actor × resource × action',
  'browser-boundaries': 'Which security mechanism decides?',
  'dom-event': 'From event to visible text',
  'promise-exchange': 'Promise, response and body',
  'response-race': 'Control the order of responses',
  'async-lanes': 'Waiting, overlap and a blocked loop',
  'socket-state': 'Connection state is not data state',
  'retry-ledger': 'Separate the caller from committed state',
  'test-boundaries': 'Match the evidence to the claim',
} as const;
export const applicationModels: Record<keyof typeof applicationModelTitles, () => ReactNode> = {
  'relationship-map': RelationshipMap,
  'transaction-journal': TransactionJournal,
  'contract-gates': ContractGates,
  'page-window': PageWindow,
  'document-compare': DocumentCompare,
  'session-timeline': SessionTimeline,
  'token-gates': TokenGates,
  'permission-matrix': PermissionMatrix,
  'browser-boundaries': BrowserBoundaries,
  'dom-event': DomEvent,
  'promise-exchange': PromiseExchange,
  'response-race': ResponseRace,
  'async-lanes': AsyncLanes,
  'socket-state': SocketState,
  'retry-ledger': RetryLedger,
  'test-boundaries': TestBoundaries,
};
